import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useCallAPI } from 'hooks/useCallAPI';
import {
  TableChangeState,
  TableChangeType
} from 'components/common/Table/types';
import {
  DeviceUnitExportQuery,
  DeviceUnitQuery,
  DeviceUnitRecord,
  DeviceUnitSortOrder
} from 'core/deviceManagement/DeviceManagement';
import {
  DefaultDeviceManagementManager,
  DeviceManagementManager
} from 'core/deviceManagement/DeviceManagementManager';
import {
  DeviceManagementFilter,
  DeviceQueryConditions,
  EMPTY_FILTER,
  toDeviceFilterParams
} from './deviceFilters';
import { DEFAULT_DEVICE_SORT, resolveTableSort } from './deviceListColumns';
import { resolveExportSort } from './deviceExport';

export const DEFAULT_PAGE_SIZE = 20;

/**
 * 清單的查詢狀態。`page` 是 0 起算 —— 送給後端的就是這個值。
 * 回應帶回來的 `pagination.page` 則是 1 起算，兩邊不一樣是既有契約，
 * 表格元件也是用 1 起算，所以進出各換一次。
 */
export type DeviceListRequest = {
  channelId?: number;
  filter: DeviceManagementFilter;
  storeNums: string[];
  page: number;
  size: number;
  sortField: string;
  sortOrder: DeviceUnitSortOrder;
  search: string;
};

/**
 * 預設排序與後端一致：狀態（依嚴重度，不是字母序）由重到輕，同狀態再依 unitKey。
 * 送一個不一樣的預設值，第一次載入看到的順序就會跟後端排出來的不同。
 */
export const INITIAL_DEVICE_LIST_REQUEST: DeviceListRequest = {
  channelId: undefined,
  filter: EMPTY_FILTER,
  storeNums: [],
  page: 0,
  size: DEFAULT_PAGE_SIZE,
  ...DEFAULT_DEVICE_SORT,
  search: ''
};

/** 表格回報的頁碼是 1 起算，查詢要的是 0 起算。 */
export const toRequestPage = (tablePage?: number): number =>
  Math.max(0, (tablePage ?? 1) - 1);

export const toQuery = (request: DeviceListRequest): DeviceUnitQuery => ({
  channelId: request.channelId as number,
  page: request.page,
  size: request.size,
  sortField: request.sortField,
  sortOrder: request.sortOrder,
  search: request.search,
  filter: toDeviceFilterParams(request.filter),
  storeNums: request.storeNums
});

/**
 * 匯出的查詢。條件與清單同一份，排序仍先過一次白名單：畫面上可能有一個後端還不認得的
 * 可排序欄位，那時退回預設排序，檔案照樣拿得到。
 */
export const toExportQuery = (
  request: DeviceListRequest,
  limit?: number
): DeviceUnitExportQuery => ({
  ..._.omit(toQuery(request), ['page', 'size']),
  ...resolveExportSort(request.sortField, request.sortOrder),
  limit
});

const defaultManager = new DefaultDeviceManagementManager();

/**
 * 匯出檔案。
 *
 * 自己持有一個 useCallAPI，不與清單查詢共用：匯出中的轉圈要跟著匯出面板走，
 * 而面板在兩個分頁都開得起來。共用清單那一個的話，在可售率分頁按下下載時，
 * 轉圈掛在一個沒被畫出來的分頁上，畫面從頭到尾沒有任何反應。
 *
 * 兩個分頁各自把自己的查詢傳進來，匯出的內容才與各自畫面上看到的那一份一致 ——
 * 兩邊的搜尋字串與排序是分開的。
 */
export const useDeviceExport = <R extends DeviceListRequest>(
  request: R,
  manager: DeviceManagementManager,
  toExportQueryOf: (
    request: R,
    limit?: number
  ) => DeviceUnitExportQuery = toExportQuery
) => {
  const { loading: exporting, callAPIs } = useCallAPI();

  const exportUnits = useCallback(
    (
      limit: number | undefined,
      fallbackFileName: string,
      onDownloaded?: () => void
    ) => {
      if (request.channelId === undefined) {
        return;
      }
      callAPIs(
        [
          () =>
            manager.exportUnits(
              toExportQueryOf(request, limit),
              fallbackFileName
            )
        ],
        onDownloaded
      );
    },
    [request, callAPIs, manager, toExportQueryOf]
  );

  return { exporting, exportUnits };
};

export const useDeviceListModel = (
  { salesChannel, filter, storeNums, active }: DeviceQueryConditions,
  manager: DeviceManagementManager = defaultManager
) => {
  const { loading, callAPIs } = useCallAPI();
  const [records, setRecords] = useState<DeviceUnitRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [request, setRequest] = useState<DeviceListRequest>(
    INITIAL_DEVICE_LIST_REQUEST
  );
  // 上一次真的送出去的那一組條件。換分頁回來時條件沒變就不重查 —— 這一層的
  // 查詢狀態掛在頁面元件上，換分頁不會卸載，所以「不重查」等於畫面原封不動。
  // 條件只要有一個字不一樣就會重查，包含渠道、篩選、名單、搜尋、排序與頁碼，
  // 所以在別的分頁改過條件之後切回來，一定會重新查。
  const lastFetched = useRef<string>();
  // 上一次看到的頁首條件（渠道、篩選、名單），只有它真的變了才把頁碼推回第一頁。
  const lastConditions = useRef<string>();

  // 頁首的渠道或篩選條件一變，整份查詢就回到第一頁 —— 留在第 5 頁的話，
  // 換條件後很可能落在一個根本不存在的頁碼上，畫面會空白。
  // 這裡與翻頁 / 排序共用同一份 state，是為了讓下面只有一支發查詢的 effect：
  // 拆成兩支會在換條件時先用舊頁碼發一次、再用新頁碼發一次。
  //
  // 比的是條件的內容而不是物件本身：上層每次 render 給一個新的（但內容一樣的）
  // 條件物件是很常見的，比物件的話每一次 render 都會把頁碼推回第一頁，
  // 使用者翻到第 3 頁會被彈回第 1 頁。
  useEffect(() => {
    const key = JSON.stringify({ salesChannel, filter, storeNums });
    if (lastConditions.current === key) {
      return;
    }
    lastConditions.current = key;
    setRequest((prev) => ({
      ...prev,
      channelId: salesChannel,
      filter,
      storeNums,
      page: 0
    }));
  }, [salesChannel, filter, storeNums]);

  useEffect(() => {
    if (!active || request.channelId === undefined) {
      return;
    }
    const query = toQuery(request);
    const key = JSON.stringify(query);
    if (lastFetched.current === key) {
      return;
    }
    lastFetched.current = key;
    callAPIs(
      [() => manager.getUnits(query)],
      (result) => {
        setFailed(false);
        setRecords(result.records);
        setTotalCount(result.pagination.totalCount);
        setLastSyncedAt(result.lastSyncedAt);
      },
      () => {
        // 失敗就不算查過。記號留著的話，使用者退回原本的條件（或切分頁再切回來）
        // 都會被「條件沒變」擋下來，重試不了。
        lastFetched.current = undefined;
        // 上一個條件查到的列不能留在畫面上：條件已經換了，那些列現在是在回答另一個問題，
        // 而畫面上沒有任何地方說得出這件事。
        setRecords([]);
        setTotalCount(0);
        setLastSyncedAt(undefined);
        // 空清單要說得出是「查不到」還是「查壞了」—— 兩者都是零列，而使用者該做的事相反。
        setFailed(true);
      }
    );
  }, [active, request, callAPIs, manager]);

  const updateRequest = useCallback((partial: Partial<DeviceListRequest>) => {
    setRequest((prev) => {
      const next = { ...prev, ...partial };
      return _.isEqual(prev, next) ? prev : next;
    });
  }, []);

  const onTableChange = useCallback(
    (type: TableChangeType, newState: TableChangeState<DeviceUnitRecord>) => {
      if (type === 'sort') {
        updateRequest({
          ...resolveTableSort(newState.sortField, newState.sortOrder),
          // 換排序等於換了一份結果，停在原本的頁碼沒有意義。
          page: 0
        });
        return;
      }
      if (type === 'pagination') {
        updateRequest({
          page: toRequestPage(newState.page),
          size: newState.sizePerPage
        });
      }
    },
    [updateRequest]
  );

  // 搜尋不會經過 onTableChange，表格是另外用 onHandleSearch 這個 prop 回報的。
  const onHandleSearch = useCallback(
    (searchString: string) => {
      updateRequest({ search: searchString, page: 0 });
    },
    [updateRequest]
  );

  const pagination = useMemo(
    () => ({
      page: request.page + 1,
      sizePerPage: request.size,
      totalSize: totalCount
    }),
    [request.page, request.size, totalCount]
  );

  const defaultSorted = useMemo(
    () =>
      [{ dataField: request.sortField, order: request.sortOrder }] as [
        { dataField: string; order: DeviceUnitSortOrder }
      ],
    // 只在第一次掛載時給表格初始排序，之後由表格自己持有，重算會把使用者
    // 點過的排序又推回去一次。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { exporting, exportUnits } = useDeviceExport(request, manager);

  return {
    loading,
    records,
    totalCount,
    lastSyncedAt,
    failed,
    request,
    pagination,
    defaultSorted,
    searchString: request.search,
    onTableChange,
    onHandleSearch,
    exporting,
    exportUnits
  };
};

export type DeviceListModel = ReturnType<typeof useDeviceListModel>;
